import React, { useState, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import ReactCountryFlag from "react-country-flag";
import { getCountries, getCountryCallingCode, CountryCode } from "libphonenumber-js";
import { downArrow } from "../../common/svg";
import SecureImage from "../../components/BucketImage/Image";

interface Country {
  name: string;
  code: string;
  dialCode: string;
}

export interface PhoneInputProps<T> {
  onChange?: (value: { countryCode: string; phoneNumber: string; dialCode: string }) => void;
  defaultCountry?: string;
  placeholder?: string;
  label?: string;
  maxLength?: number;
  name: string;
  rules?: any;
  errors?: any;
  control: any;
  type?: "tel" | "text";
  value?: string;
  defaultValue?: { countryCode: string; phoneNumber: string; dialCode?: string };
}

const PhoneInput = <T,>({
  onChange,
  defaultCountry = "+0",
  placeholder = "Enter phoneNumber",
  label,
  maxLength = 10,
  name,
  rules,
  errors,
  control,
  type = "tel",
  value,
  defaultValue,
}: PhoneInputProps<T>) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(value || defaultValue?.phoneNumber || "");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadCountries = () => {
      try {
        const countryList = getCountries().map((code) => ({
          code,
          name: new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code,
          dialCode: `+${getCountryCallingCode(code as CountryCode)}`,
        }));
        setCountries(countryList);

        const targetDialCode = defaultValue?.dialCode || defaultValue?.countryCode || defaultCountry;

        if (targetDialCode && targetDialCode !== "+0") {
          const foundCountry = countryList.find(c => c.dialCode === targetDialCode);
          if (foundCountry) {
            setSelectedCountry(foundCountry);
          } else if (defaultCountry && defaultCountry !== "+0") {
            const country = countryList.find(c =>
              defaultCountry.startsWith("+")
                ? c.dialCode === defaultCountry
                : c.code === defaultCountry
            );

            if (country) {
              setSelectedCountry(country);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load countries:", error);
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.dialCode.includes(searchQuery) ||
      country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery("");

    if (onChange) {
      onChange({
        countryCode: country.dialCode,
        phoneNumber: phoneNumber || "",
        dialCode: country.dialCode,
      });
    }
  };

  const handlePhoneChange = (value: string, controllerOnChange: (value: string) => void) => {
    const formatted = value.replace(/\D/g, "").slice(0, maxLength);
    setPhoneNumber(formatted);
    controllerOnChange(formatted);

    if (onChange && selectedCountry) {
      onChange({
        countryCode: selectedCountry.dialCode,
        phoneNumber: formatted,
        dialCode: selectedCountry.dialCode,
      });
    }
  };

  const inputError = errors?.[name];

  return (
    <div className="w-full max-w-md">
      {label && (
        <label className="text-[#282828] font-[600] font-poppins text-[14px]">
          {label}
        </label>
      )}
      <div className="flex gap-2 mt-2">
        {/* Country Selector */}
        <div
          className={`relative flex items-center gap-2 w-[130px] h-[40px] px-2 text-sm border rounded-md bg-white ${
            inputError ? "border-red-500" : "border-gray-300"
          }`}
          ref={dropdownRef}
        >
          <button
            type="button"
            className="flex items-center w-full justify-between"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className="flex items-center">
              {selectedCountry ? (
                <>
                  <ReactCountryFlag countryCode={selectedCountry.code} svg className="w-5 h-5" />
                  <span className="ml-1 text-gray-600">{selectedCountry.dialCode}</span>
                </>
              ) : (
                <span className="ml-1 text-gray-600">+0</span>
              )}
            </div>
            <SecureImage fileKey={downArrow} alt="down arrow" width="20px" className="ml-3" />
          </button>

          {isDropdownOpen && (
            <div className="absolute left-0 top-full bg-white border rounded shadow-md z-20 mt-1 w-60">
              <input
                type="text"
                className="w-full p-2 text-sm border-b border-gray-300 focus:outline-none"
                placeholder="Search countries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="max-h-60 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <li
                      key={country.code}
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer list-none"
                      onClick={() => handleCountrySelect(country)}
                    >
                      <ReactCountryFlag countryCode={country.code} svg className="w-5 h-5" />
                      <span className="flex-1 truncate">{country.name}</span>
                      <span className="text-gray-500">{country.dialCode}</span>
                    </li>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">No countries found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <Controller
          control={control}
          name={name}
          rules={rules || { required: "Phone number is required" }}
          defaultValue={value ?? defaultValue?.phoneNumber ?? ""}
          render={({ field: { value: fieldValue = "", onChange: controllerOnChange, onBlur } }) => (
            <input
              type={type}
              className={`w-full p-1 text-sm border rounded-md  focus:outline-none ${
                inputError ? "border-red-500 focus:ring-red-300" : "border-gray-300"
              }`}
              placeholder={placeholder}
              value={fieldValue}
              onChange={(e) => handlePhoneChange(e.target.value, controllerOnChange)}
              onBlur={onBlur}
              maxLength={maxLength}
            />
          )}
        />
      </div>
      {inputError && (
        <p className=" text-[#ef4444] text-[12px] mt-1.5 whitespace-nowrap">{inputError.message}</p>
      )}
    </div>
  );
};

export default PhoneInput;
